import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { Building2, User, Package, FileText, AlertTriangle, Loader2, Mail, MessageSquare } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CustomerActions } from '@/app/(dashboard)/customers/[id]/_components/CustomerActions'
import { CustomerTabs } from '@/app/(dashboard)/customers/[id]/_components/CustomerTabs'
import { CustomerEditForm } from '@/app/(dashboard)/customers/[id]/_components/CustomerEditForm'
import { AuditLogTable } from '@/app/(dashboard)/customers/[id]/_components/AuditLogTable'
import { apiClient } from '@/lib/api/client'
import type { Consignment, Customer, HelmCommunication, Profile } from '@/lib/api/types'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'

type CustomerContext = {
  customer: Customer
  profile: Profile | null
  consignments: Consignment[]
  communications: HelmCommunication[]
  reload: () => void
}

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function useCustomerContext() {
  return useOutletContext<CustomerContext>()
}

function normalizeWhatsAppPhone(phone: string | null | undefined) {
  return phone?.replace(/\D/g, '') || ''
}

function CustomerCommunicationsCard({
  customer,
  communications,
  isAdmin,
  onChanged,
}: {
  customer: Customer
  communications: HelmCommunication[]
  isAdmin: boolean
  onChanged: () => void
}) {
  const displayName = customer.company_name || customer.name
  const defaultSubject = `Helm update for ${displayName}`
  const defaultBody = `Hi ${displayName},\n\nWe have an update on your Helm inquiry.\n\nRegards,\nXtellar Helm`
  const whatsappPhone = normalizeWhatsAppPhone(customer.phone)
  const [emailSubject, setEmailSubject] = useState(defaultSubject)
  const [emailBody, setEmailBody] = useState(defaultBody)
  const [whatsappBody, setWhatsappBody] = useState(defaultBody)
  const [submitting, setSubmitting] = useState<'EMAIL' | 'WHATSAPP' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleQueueEmail(event: FormEvent) {
    event.preventDefault()
    setSubmitting('EMAIL')
    setError(null)
    try {
      await apiClient.createCommunication({
        customer_id: customer.id,
        channel: 'EMAIL',
        status: 'QUEUED',
        template_key: 'HELM_CUSTOMER_UPDATE',
        recipient: customer.email,
        subject: emailSubject.trim() || defaultSubject,
        body: emailBody.trim(),
      })
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to queue customer email')
    } finally {
      setSubmitting(null)
    }
  }

  async function handleOpenWhatsApp() {
    if (!whatsappPhone) {
      setError('Customer phone is not set')
      return
    }

    setSubmitting('WHATSAPP')
    setError(null)
    const body = whatsappBody.trim() || defaultBody
    try {
      await apiClient.createCommunication({
        customer_id: customer.id,
        channel: 'WHATSAPP',
        status: 'LOGGED',
        template_key: 'HELM_CUSTOMER_WHATSAPP_UPDATE',
        recipient: `+${whatsappPhone}`,
        body,
      })
      window.open(`https://wa.me/${whatsappPhone}?text=${encodeURIComponent(body)}`, '_blank', 'noopener,noreferrer')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to log customer WhatsApp contact')
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Communications</CardTitle>
        <CardDescription>Customer-level email queue and WhatsApp contact history.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isAdmin ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <form onSubmit={handleQueueEmail} className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">Email</div>
                  <div className="text-sm text-muted-foreground">{customer.email}</div>
                </div>
                <Button type="submit" size="sm" disabled={submitting !== null || !customer.email}>
                  {submitting === 'EMAIL' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Queue
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email-subject">Subject</Label>
                <Input
                  id="customer-email-subject"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  disabled={submitting !== null}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email-body">Message</Label>
                <Textarea
                  id="customer-email-body"
                  value={emailBody}
                  onChange={(event) => setEmailBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={5}
                />
              </div>
            </form>

            <div className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">WhatsApp</div>
                  <div className="text-sm text-muted-foreground">{customer.phone || 'No phone on customer'}</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={handleOpenWhatsApp} disabled={submitting !== null || !whatsappPhone}>
                  {submitting === 'WHATSAPP' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                  Open
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-whatsapp-body">Message</Label>
                <Textarea
                  id="customer-whatsapp-body"
                  value={whatsappBody}
                  onChange={(event) => setWhatsappBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={7}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            Admin access is required to queue or log customer communications.
          </div>
        )}

        <div className="divide-y rounded-md border">
          {communications.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No customer-level communications have been recorded.</p>
          ) : (
            communications.slice(0, 10).map((communication) => (
              <div key={communication.id} className="space-y-2 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={communication.channel === 'WHATSAPP' ? 'secondary' : 'outline'}>
                    {formatLabel(communication.channel)}
                  </Badge>
                  <Badge variant="outline">{formatLabel(communication.status)}</Badge>
                  <span className="text-sm text-muted-foreground">
                    {new Date(communication.sent_at || communication.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">{communication.recipient}</div>
                {communication.subject && <div className="font-medium">{communication.subject}</div>}
                <p className="whitespace-pre-wrap text-sm">{communication.body}</p>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function CustomerDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsyncData(async () => {
    if (!id) throw new Error('Customer id is missing')

    const [customer, profile, consignments, communications] = await Promise.all([
      apiClient.getCustomer(id),
      apiClient.getProfile().catch(() => null),
      apiClient.listConsignments().catch(() => []),
      apiClient.listCommunications({ customer_id: id, limit: 50 }).catch(() => []),
    ])

    return {
      customer,
      profile,
      consignments: consignments.filter((consignment) => consignment.customer_id === id),
      communications,
    }
  }, [id])

  if (loading) return <LoadingState label="Loading customer..." />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data?.customer || !id) return <ErrorState error="Customer not found" />

  const { customer, profile, consignments, communications } = data
  const isAdmin = profile?.is_admin || profile?.role === 'admin'
  const isArchived = !!customer.deleted_at
  const displayName = customer.company_name || customer.name
  const invoiceCount = consignments.filter((consignment) => consignment.invoice_url || consignment.original_invoice_url).length

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={displayName}
        description={customer.type === 'BUSINESS' ? customer.name : customer.email}
        breadcrumbs={[
          { label: 'Customers', href: '/customers' },
          { label: displayName },
        ]}
        actions={(
          <CustomerActions
            customer={customer}
            isAdmin={isAdmin}
            onChanged={reload}
            onDeleted={() => navigate('/customers')}
          />
        )}
      />

      <div className="flex-1 space-y-4 overflow-auto p-4 md:p-6">
        {isArchived && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This customer is archived. They are hidden from the default customer list.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={customer.type === 'BUSINESS' ? 'default' : 'secondary'}>
            {customer.type === 'BUSINESS' ? (
              <><Building2 className="mr-1 h-3 w-3" /> Business</>
            ) : (
              <><User className="mr-1 h-3 w-3" /> Individual</>
            )}
          </Badge>
          {isArchived && <Badge variant="destructive">Archived</Badge>}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Package className="h-3.5 w-3.5" />
                Consignments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{consignments.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                Invoices
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{invoiceCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>TIN Number</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-mono">{customer.tin_number}</div>
            </CardContent>
          </Card>
        </div>

        <CustomerTabs customerId={id} consignmentsCount={consignments.length} invoicesCount={invoiceCount} />

        <Outlet context={{ customer, profile, consignments, communications, reload } satisfies CustomerContext} />
      </div>
    </div>
  )
}

export function CustomerOverviewPage() {
  const { customer, profile, communications, reload } = useCustomerContext()
  const isAdmin = Boolean(profile?.is_admin || profile?.role === 'admin')

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Customer Details</CardTitle>
        </CardHeader>
        <CardContent>
          <CustomerEditForm
            customer={customer}
            canEdit={profile?.is_admin || profile?.role === 'admin'}
            onSaved={reload}
          />
        </CardContent>
      </Card>
      <CustomerCommunicationsCard
        key={customer.id}
        customer={customer}
        communications={communications}
        isAdmin={isAdmin}
        onChanged={reload}
      />
    </div>
  )
}

export function CustomerConsignmentsPage() {
  const { consignments } = useCustomerContext()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consignments</CardTitle>
      </CardHeader>
      <CardContent>
        {consignments.length === 0 ? (
          <p className="text-muted-foreground">No consignments recorded for this customer.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {consignments.map((consignment) => (
              <div key={consignment.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-2">
                  <div className="font-medium">
                    {consignment.international_tracking_number || consignment.tracking_number || `Consignment ${consignment.id.slice(0, 8)}`}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{formatLabel(consignment.service_type)}</Badge>
                    <Badge variant="outline">{formatLabel(consignment.inquiry_stage)}</Badge>
                    <Badge variant="outline">{formatLabel(consignment.preferred_communication_channel)}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    <span>{consignment.destination_address || consignment.destination_port}</span>
                    <span>{formatLabel(consignment.status)}</span>
                    <span>{new Date(consignment.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    <span>Owner: {consignment.owner_user_id || 'Unassigned'}</span>
                    <span>Next: {consignment.next_action || 'Not set'}</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <Link to={`/consignments/${consignment.id}`}>Open</Link>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function CustomerInvoicesPage() {
  const { consignments } = useCustomerContext()
  const invoices = consignments.filter((consignment) => consignment.invoice_url || consignment.original_invoice_url)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
      </CardHeader>
      <CardContent>
        {invoices.length === 0 ? (
          <p className="text-muted-foreground">No invoice documents found for this customer.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {invoices.map((consignment) => (
              <div key={consignment.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-medium">
                    {consignment.tracking_number || `Consignment ${consignment.id.slice(0, 8)}`}
                  </div>
                  <div className="text-sm text-muted-foreground">{new Date(consignment.created_at).toLocaleDateString()}</div>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <a href={consignment.invoice_url || consignment.original_invoice_url || '#'} target="_blank" rel="noreferrer">
                    Open Invoice
                  </a>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function CustomerActivityPage() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Log</CardTitle>
      </CardHeader>
      <CardContent>
        <AuditLogTable logs={[]} />
      </CardContent>
    </Card>
  )
}
